import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Plus, Trash2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../../api/client';
import { getCurrencySymbol } from '../../utils/currency';
import { useAuth } from '../../contexts/AuthContext';

const InvoiceModal = ({ open, onClose, invoice, onSuccess }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    project_id: '',
    client_id: '',
    client_name: '',
    client_email: '',
    client_address: '',
    due_date: '',
    items: [{ description: '', quantity: 1, unit_price: 0, amount: 0 }],
    tax_rate: 0,
    notes: '',
    currency: user?.currency || 'INR',
    gstin_supplier: user?.gstin || '',
    gstin_recipient: '',
    place_of_supply: '',
    is_reverse_charge: false
  });
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTax, setShowTax] = useState(false);
  const [manualClient, setManualClient] = useState(false);

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open]);

  useEffect(() => {
    if (invoice) {
      setFormData({
        project_id: invoice.project_id || '',
        client_id: invoice.client_id || '',
        client_name: invoice.client_name || '',
        client_email: invoice.client_email || '',
        client_address: invoice.client_address || '',
        due_date: invoice.due_date ? new Date(invoice.due_date).toISOString().split('T')[0] : '',
        items: invoice.items || [{ description: '', quantity: 1, unit_price: 0, amount: 0 }],
        tax_rate: invoice.tax_rate || 0,
        notes: invoice.notes || '',
        currency: invoice.currency || user?.currency || 'INR',
        gstin_supplier: invoice.gstin_supplier || user?.gstin || '',
        gstin_recipient: invoice.gstin_recipient || '',
        place_of_supply: invoice.place_of_supply || '',
        is_reverse_charge: invoice.is_reverse_charge || false
      });
      if (invoice.tax_rate > 0 || invoice.gstin_supplier) setShowTax(true);
      if (!invoice.client_id && invoice.client_name) setManualClient(true);
    } else {
      setFormData({
        project_id: '',
        client_id: '',
        client_name: '',
        client_email: '',
        client_address: '',
        due_date: '',
        items: [{ description: '', quantity: 1, unit_price: 0, amount: 0 }],
        tax_rate: 0,
        notes: '',
        currency: user?.currency || 'INR',
        gstin_supplier: user?.gstin || '',
        gstin_recipient: '',
        place_of_supply: '',
        is_reverse_charge: false
      });
      setManualClient(false);
    }
  }, [invoice, open, user]);

  const fetchData = async () => {
    try {
      const [projRes, clientRes] = await Promise.all([
        apiClient.get('/projects'),
        apiClient.get('/clients')
      ]);
      setProjects(projRes.data.items || projRes.data);
      setClients(clientRes.data.items || clientRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  const handleProjectChange = (val) => {
    if (val === 'none') {
        setFormData({ ...formData, project_id: null });
        return;
    }
    const project = projects.find(p => p.project_id === val);
    if (project) {
        setFormData({
            ...formData,
            project_id: val,
            client_id: project.client_id,
            manualClient: false
        });
        setManualClient(false);
    } else {
        setFormData({ ...formData, project_id: val });
    }
  };

  const handleClientChange = (val) => {
      if (val === 'manual') {
          setManualClient(true);
          setFormData({ ...formData, client_id: null, project_id: null });
      } else {
          setManualClient(false);
          setFormData({ ...formData, client_id: val });
      }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;

    if (field === 'quantity' || field === 'unit_price') {
      newItems[index].amount = newItems[index].quantity * newItems[index].unit_price;
    }

    setFormData({ ...formData, items: newItems });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit_price: 0, amount: 0 }]
    });
  };

  const removeItem = (index) => {
    if (formData.items.length === 1) return;
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const taxAmount = subtotal * (parseFloat(formData.tax_rate) / 100);
    const total = subtotal + taxAmount;
    return { subtotal, taxAmount, total };
  };

  const { subtotal, taxAmount, total } = calculateTotals();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!manualClient && !formData.client_id) return toast.error('Please select a client');
    if (manualClient && (!formData.client_name || !formData.client_email)) {
        return toast.error('Please provide client name and email for manual entry');
    }
    if (!formData.due_date) return toast.error('Please select a due date');
    if (formData.items.some(item => !item.description || item.amount <= 0)) {
        return toast.error('Please ensure all items have descriptions and amounts > 0');
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        project_id: formData.project_id === 'none' ? null : (formData.project_id || null),
        client_id: manualClient ? null : formData.client_id,
        due_date: new Date(formData.due_date).toISOString(),
        tax_rate: parseFloat(formData.tax_rate)
      };

      if (invoice) {
        await apiClient.put(`/invoices/${invoice.invoice_id}`, submitData);
        toast.success('Invoice updated successfully');
      } else {
        await apiClient.post('/invoices', submitData);
        toast.success('Invoice created successfully');
      }
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save invoice');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[800px] max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{invoice ? 'Edit Invoice' : 'Create New Invoice'}</DialogTitle>
          <DialogDescription>
            Generate a formal invoice. You can select a client or enter details manually.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="client">Client *</Label>
              <Select value={manualClient ? 'manual' : (formData.client_id || '')} onValueChange={handleClientChange}>
                <SelectTrigger className="bg-slate-50">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}
                  <SelectItem value="manual" className="text-indigo-600 font-medium">
                      <div className="flex items-center">
                          <UserPlus className="h-4 w-4 mr-2" /> Manual Entry
                      </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="project">Project (Optional)</Label>
              <Select
                  value={formData.project_id || 'none'}
                  onValueChange={handleProjectChange}
                  disabled={manualClient}
              >
                <SelectTrigger className="bg-slate-50">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {projects.filter(p => !formData.client_id || p.client_id === formData.client_id).map(p => (
                    <SelectItem key={p.project_id} value={p.project_id}>{p.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="due_date">Due Date *</Label>
              <Input
                id="due_date"
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                className="bg-slate-50"
              />
            </div>
          </div>

          {manualClient && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg border border-dashed border-slate-300">
                  <div className="space-y-1">
                      <Label className="text-xs">Client Name *</Label>
                      <Input
                        value={formData.client_name}
                        onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                        placeholder="John Doe"
                        className="bg-white h-9"
                      />
                  </div>
                  <div className="space-y-1">
                      <Label className="text-xs">Client Email *</Label>
                      <Input
                        type="email"
                        value={formData.client_email}
                        onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                        placeholder="john@example.com"
                        className="bg-white h-9"
                      />
                  </div>
                  <div className="space-y-1">
                      <Label className="text-xs">Client Address</Label>
                      <Input
                        value={formData.client_address}
                        onChange={(e) => setFormData({ ...formData, client_address: e.target.value })}
                        placeholder="123 Street, City"
                        className="bg-white h-9"
                      />
                  </div>
              </div>
          )}

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <Label className="text-base font-semibold">Billable Items</Label>
              <Button type="button" variant="outline" size="sm" onClick={addItem} className="text-indigo-600 border-indigo-200">
                <Plus className="h-4 w-4 mr-1" /> Add Item
              </Button>
            </div>

            <div className="space-y-3">
              {formData.items.map((item, index) => (
                <div key={index} className="flex gap-3 items-start bg-slate-50 p-3 rounded-lg border border-slate-100">
                  <div className="flex-[3] space-y-1">
                    <Label className="text-[10px] uppercase text-slate-500">Description</Label>
                    <Input
                      placeholder="Item description"
                      value={item.description}
                      onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                      className="h-9 bg-white"
                    />
                  </div>
                  <div className="flex-1 space-y-1">
                    <Label className="text-[10px] uppercase text-slate-500">Qty</Label>
                    <Input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                      className="h-9 bg-white"
                    />
                  </div>
                  <div className="flex-[1.5] space-y-1">
                    <Label className="text-[10px] uppercase text-slate-500">Price</Label>
                    <Input
                      type="number"
                      min="0"
                      value={item.unit_price}
                      onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                      className="h-9 bg-white"
                    />
                  </div>
                  <div className="flex-[1.5] space-y-1">
                    <Label className="text-[10px] uppercase text-slate-500">Total</Label>
                    <div className="h-9 flex items-center px-3 bg-slate-100 rounded-md font-medium text-slate-700">
                      {getCurrencySymbol(formData.currency)}{item.amount.toLocaleString()}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeItem(index)}
                    className="mt-6 text-slate-400 hover:text-rose-500"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-4">
                <div className="flex items-center space-x-2">
                    <Checkbox
                        id="show-tax"
                        checked={showTax}
                        onCheckedChange={setShowTax}
                    />
                    <Label htmlFor="show-tax" className="cursor-pointer">Add Tax / GST Details</Label>
                </div>

                {showTax && (
                    <div className="space-y-3 p-4 bg-indigo-50/50 rounded-lg border border-indigo-100">
                        <div className="space-y-1">
                            <Label className="text-xs">Tax Rate (%)</Label>
                            <Input
                                type="number"
                                value={formData.tax_rate}
                                onChange={(e) => setFormData({ ...formData, tax_rate: e.target.value })}
                                className="h-9 bg-white"
                            />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Your GSTIN</Label>
                            <Input
                                value={formData.gstin_supplier}
                                onChange={(e) => setFormData({ ...formData, gstin_supplier: e.target.value })}
                                placeholder="Supplier GSTIN"
                                className="h-9 bg-white"
                            />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Client GSTIN</Label>
                            <Input
                                value={formData.gstin_recipient}
                                onChange={(e) => setFormData({ ...formData, gstin_recipient: e.target.value })}
                                placeholder="Recipient GSTIN"
                                className="h-9 bg-white"
                            />
                        </div>
                    </div>
                )}

                <div className="space-y-2">
                    <Label htmlFor="notes">Notes / Terms</Label>
                    <Textarea
                        id="notes"
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        placeholder="Payment terms, bank details, etc."
                        rows={3}
                        className="bg-slate-50"
                    />
                </div>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl border border-slate-100 space-y-3">
                <div className="flex justify-between text-slate-600">
                    <span>Subtotal</span>
                    <span className="font-semibold">{getCurrencySymbol(formData.currency)}{subtotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-slate-600">
                    <span>Tax ({formData.tax_rate}%)</span>
                    <span className="font-semibold">{getCurrencySymbol(formData.currency)}{taxAmount.toLocaleString()}</span>
                </div>
                <div className="pt-3 border-t border-slate-200 flex justify-between items-center">
                    <span className="text-lg font-bold text-slate-900">Total Amount</span>
                    <span className="text-2xl font-black text-indigo-600">
                        {getCurrencySymbol(formData.currency)}{total.toLocaleString()}
                    </span>
                </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={loading}>
              {loading ? 'Processing...' : invoice ? 'Update Invoice' : 'Create Invoice'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceModal;
